import React, {
    FC,
    useCallback,
    useEffect,
    useId,
    useLayoutEffect,
    useRef,
    useState,
} from "react";
import { TooltipPlaceE, IconColorE } from "../../models/enums";
import Icon from "../Icon/Icon";
import Portal from "../Portal/Portal";
import Button from "../Button/Button";
import { isMobileOrTablet } from "../../utils/helpers/mediaQueries";
import { useTranslate } from "../../utils/hooks/useTranslate";
import { clss } from "../../utils/helpers/styleHelpers";

import styles from "./infoTooltip.module.scss";

/**
 * Shows tooltip with info on hover about an input field or something else.
 * On mobile/tablet, opens a bottom sheet in a portal with close (X), slide-in animation,
 * and swipe-down-to-dismiss (must pass threshold or snaps back). Sheet follows finger while dragging.
 */

type InfoTooltipPropTypes = {
    text: string;
    place?: TooltipPlaceE;
    addAction?: () => void;
    /** When true, `text` is shown as-is (no translation). */
    withoutTranslation?: boolean;
};

const DISMISS_RATIO = 0.22;
const MIN_DISMISS_PX = 72;
const OPEN_DURATION_MS = 320;
const SNAP_DURATION_MS = 280;

type MobileBottomSheetProps = {
    onClose: () => void;
    titleId: string;
    displayText: string;
    addAction?: () => void;
    onAdd: () => void;
    closeLabel: string;
    addLabel: string;
};

const MobileBottomSheet: FC<MobileBottomSheetProps> = ({
    onClose,
    titleId,
    displayText,
    addAction,
    onAdd,
    closeLabel,
    addLabel,
}) => {
    const sheetRef = useRef<HTMLDivElement>(null);
    const startYRef = useRef(0);
    const dragOriginTranslateRef = useRef(0);
    const translateYRef = useRef(0);
    const sheetHeightRef = useRef(400);
    const isClosingRef = useRef(false);
    const didNotifyCloseRef = useRef(false);
    const draggingRef = useRef(false);

    const [translateY, setTranslateY] = useState(0);
    const [transitionMs, setTransitionMs] = useState(0);
    const [isDragging, setIsDragging] = useState(false);
    const [isClosing, setIsClosing] = useState(false);
    const [backdropVisible, setBackdropVisible] = useState(false);

    useEffect(() => {
        translateYRef.current = translateY;
    }, [translateY]);

    useEffect(() => {
        isClosingRef.current = isClosing;
    }, [isClosing]);

    const notifyClose = useCallback(() => {
        if (didNotifyCloseRef.current) return;
        didNotifyCloseRef.current = true;
        onClose();
    }, [onClose]);

    // Measure sheet, start below viewport, then slide up + fade backdrop
    useLayoutEffect(() => {
        const el = sheetRef.current;
        if (!el) return;

        const h = el.offsetHeight || 320;
        sheetHeightRef.current = h;
        didNotifyCloseRef.current = false;
        isClosingRef.current = false;
        draggingRef.current = false;
        setIsClosing(false);
        setBackdropVisible(false);

        setTransitionMs(0);
        setTranslateY(h);
        translateYRef.current = h;

        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                setTransitionMs(OPEN_DURATION_MS);
                setBackdropVisible(true);
                setTranslateY(0);
                translateYRef.current = 0;
            });
        });
    }, []);

    const runCloseAnimation = useCallback(() => {
        if (isClosingRef.current) return;
        isClosingRef.current = true;
        draggingRef.current = false;
        setIsClosing(true);
        setIsDragging(false);
        const h = sheetHeightRef.current;
        setTransitionMs(SNAP_DURATION_MS);
        setTranslateY(h);
        translateYRef.current = h;
    }, []);

    const handleCloseClick = useCallback(() => {
        runCloseAnimation();
    }, [runCloseAnimation]);

    const handleAddClick = useCallback(() => {
        onAdd();
        runCloseAnimation();
    }, [onAdd, runCloseAnimation]);

    const onDragHandleTouchStart = useCallback((e: React.TouchEvent) => {
        if (isClosingRef.current) return;
        const y = e.touches[0].clientY;
        startYRef.current = y;
        dragOriginTranslateRef.current = translateYRef.current;
        draggingRef.current = true;
        setIsDragging(true);
        setTransitionMs(0);
    }, []);

    const onDragHandleTouchMove = useCallback((e: React.TouchEvent) => {
        if (!draggingRef.current || isClosingRef.current) return;
        const y = e.touches[0].clientY;
        const next = Math.max(
            0,
            dragOriginTranslateRef.current + (y - startYRef.current)
        );
        setTranslateY(next);
    }, []);

    const onDragHandleTouchEnd = useCallback(() => {
        if (!draggingRef.current || isClosingRef.current) return;
        draggingRef.current = false;
        setIsDragging(false);

        const y = translateYRef.current;
        const threshold = Math.max(
            sheetHeightRef.current * DISMISS_RATIO,
            MIN_DISMISS_PX
        );

        if (y >= threshold) {
            runCloseAnimation();
        } else {
            setTransitionMs(SNAP_DURATION_MS);
            setTranslateY(0);
            translateYRef.current = 0;
        }
    }, [runCloseAnimation]);

    const onSheetTransitionEnd = useCallback(
        (e: React.TransitionEvent<HTMLDivElement>) => {
            if (e.propertyName !== "transform") return;
            if (!isClosingRef.current) return;
            const h = sheetHeightRef.current;
            if (translateYRef.current >= h - 2) {
                notifyClose();
            }
        },
        [notifyClose]
    );

    useEffect(() => {
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === "Escape") runCloseAnimation();
        };
        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, [runCloseAnimation]);

    const dragBackdropOpacity =
        0.45 *
        Math.max(0, Math.min(1, 1 - translateY / Math.max(sheetHeightRef.current, 1)));

    return (
        <>
            <div
                className={clss([
                    styles.backdrop,
                    backdropVisible && !isClosing && styles.backdropVisible,
                    isClosing && styles.backdropExit,
                ])}
                onClick={handleCloseClick}
                aria-hidden="true"
                style={
                    isDragging && !isClosing
                        ? {
                              opacity: dragBackdropOpacity,
                              transition: "none",
                              background: "#000",
                          }
                        : undefined
                }
            />
            <div
                ref={sheetRef}
                className={clss([
                    styles.bottomSheet,
                    isDragging && styles.bottomSheetDragging,
                ])}
                role="dialog"
                aria-modal="true"
                aria-labelledby={titleId}
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
                style={{
                    transform: `translate3d(0, ${translateY}px, 0)`,
                    transition:
                        isDragging || transitionMs === 0
                            ? "none"
                            : `transform ${transitionMs}ms cubic-bezier(0.32, 0.72, 0, 1)`,
                }}
                onTransitionEnd={onSheetTransitionEnd}
            >
                <div
                    className={styles.dragHandleArea}
                    onTouchStart={onDragHandleTouchStart}
                    onTouchMove={onDragHandleTouchMove}
                    onTouchEnd={onDragHandleTouchEnd}
                    onTouchCancel={onDragHandleTouchEnd}
                >
                    <div className={styles.dragHandle} aria-hidden />
                </div>
                <div className={styles.bottomSheetHeader}>
                    <button
                        type="button"
                        className={styles.closeButton}
                        onClick={handleCloseClick}
                        aria-label={closeLabel}
                    >
                        <Icon
                            name="close"
                            color={IconColorE.GRAY}
                            isShowTooltip={false}
                        />
                    </button>
                </div>
                <div id={titleId} className={styles.bottomSheetBody}>
                    {displayText}
                </div>
                {addAction && (
                    <div className={styles.bottomSheetActions}>
                        <Button type="button" onClick={handleAddClick}>
                            {addLabel}
                        </Button>
                    </div>
                )}
            </div>
        </>
    );
};

const InfoTooltip: FC<InfoTooltipPropTypes> = ({
    text,
    place = TooltipPlaceE.TOP,
    addAction,
    withoutTranslation = false,
}) => {
    const { t } = useTranslate();
    const [open, setOpen] = useState(false);
    const titleId = useId();
    const isMobile = isMobileOrTablet();

    const displayText = withoutTranslation ? text : t(text);

    const close = useCallback(() => setOpen(false), []);

    const openSheet = useCallback((e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setOpen(true);
    }, []);

    const handleAddInSheet = useCallback(() => {
        addAction?.();
    }, [addAction]);

    const mobileSheet = open && (
        <Portal id="root">
            <MobileBottomSheet
                onClose={close}
                titleId={titleId}
                displayText={displayText}
                addAction={addAction}
                onAdd={handleAddInSheet}
                closeLabel={t`Close`}
                addLabel={t`Add`}
            />
        </Portal>
    );

    if (isMobile) {
        return (
            <>
                {addAction ? (
                    <Icon
                        name="addCircleFilledBlue"
                        className={styles.addIcon}
                        onClick={openSheet}
                        isShowTooltip={false}
                    />
                ) : (
                    <div
                        className={styles.wrapper}
                        onClick={openSheet}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                setOpen(true);
                            }
                        }}
                        aria-label={t`More information`}
                    >
                        <Icon name="info" isShowTooltip={false} />
                    </div>
                )}
                {mobileSheet}
            </>
        );
    }

    return addAction ? (
        <Icon
            name="addCircleFilledBlue"
            className={styles.addIcon}
            onClick={addAction}
            title={text}
            tooltipPlace={place}
            isShowTooltip
            withoutTranslation={withoutTranslation}
        />
    ) : (
        <div className={styles.wrapper}>
            <Icon
                name="info"
                title={text}
                tooltipPlace={place}
                isShowTooltip
                withoutTranslation={withoutTranslation}
            />
        </div>
    );
};

export default InfoTooltip;
